/** @jsxImportSource @emotion/react */
import React, { useEffect, useState, useMemo, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { tb } from 'components/layouts/Table';
import { SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { MdIvestOtherSite } from 'models/md/MdInvestResult';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { getMdInvestResultOtherSite } from 'apis/md/MdInvestResult';
import { formatWonToEok } from 'utils/FormatUtil';

/**
 * @desc 투자비 산출 등록/조회 > 투자비 산출 등록 > 투자비 산출 결과 분석 탭 > 타 Site 투자비 반영 내역
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.05.14   jdseong       신규 생성
 */
interface Props {
  elmPrjId?: string;
  ivesMdlId?: string;
}

const OtherSiteCostSummary = ({ elmPrjId, ivesMdlId, ...props }: Props, ref) => {
  const { t } = useTranslation();
  const [rowData, setRowData] = useState<MdIvestOtherSite[]>([]);
  const [code, setCode] = useState<any>();

  useImperativeHandle(ref, () => ({
    search: (elmPrjId, ivesMdlId) => {
      handleSearch(elmPrjId, ivesMdlId);
    },
  }));

  useEffect(() => {
    getCommonCodes();
  }, []);

  const defaultRowData = useMemo(() => {
    const rows = (code?.prdnProcCd || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          prdnProCd: cur.cmnCd,
          prdnProcCdNm: cur.cmnCdNm,
          atcCnt: 0,
          totalInvstAmt: 0,
        },
      ],
      [] as MdIvestOtherSite[]
    );

    // 합계 행 추가
    return [
      ...rows,
      {
        prdnProCd: 'TOTAL',
        prdnProcCdNm: String(t('md.label.합계', '합계')),
        atcCnt: rows.reduce((acc, cur) => acc + cur.atcCnt, 0),
        totalInvstAmt: rows.reduce((acc, cur) => acc + cur.totalInvstAmt, 0),
      },
    ];
  }, [code]);

  const gridData = useMemo(() => {
    // 합계 행 추가
    return [
      ...(rowData || []),
      {
        prdnProCd: 'TOTAL',
        prdnProcCdNm: String(t('md.label.합계', '합계')),
        atcCnt: (rowData || []).reduce((acc, cur) => acc + Number(cur?.atcCnt || 0), 0),
        totalInvstAmt: (rowData || [])
          .reduce((acc, cur) => acc + Number(cur?.totalInvstAmt || 0), 0)
          .toFixed(1),
      },
    ];
  }, [rowData]);

  useEffect(() => {
    handleSearch(elmPrjId, ivesMdlId);
  }, [elmPrjId, ivesMdlId]);

  const getCommonCodes = async () => {
    const prdnProcCd: Code[] = await getCommonCodeNamesCondition({
      cmnGrCd: 'PRDN_PROC_CD',
      optValCtn7: 'Y', // 형개발투자비산출 공정
    });
    setCode({
      prdnProcCd: prdnProcCd,
    });
  };

  const handleSearch = (elmPrjId, ivesMdlId) => {
    getMdInvestResultOtherSite(elmPrjId, ivesMdlId).then((result) => setRowData(result));
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.타 Site 투자비 반영 내역', '타 Site 투자비 반영 내역')}</h3>
        </SubTitleGroup>
      </SubTitleLayout>
      <TableContainer css={tb.tableCustom}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              {((rowData || []).length < 1 ? defaultRowData : gridData).map((o) => (
                <>
                  <TableCell>{o.prdnProcCdNm}</TableCell>
                </>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{String(t('md.label.건수', '건수'))}</TableCell>
              {((rowData || []).length < 1 ? defaultRowData : gridData).map((o) => (
                <>
                  <TableCell>{o.atcCnt}</TableCell>
                </>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>{String(t('md.label.투자비(억원)', '투자비(억원)'))}</TableCell>
              {((rowData || []).length < 1 ? defaultRowData : gridData).map((o) => (
                <>
                  <TableCell>{o.totalInvstAmt}</TableCell>
                </>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default React.forwardRef(OtherSiteCostSummary);
